/**
 * House print rarities. A 2002 booster keeps Common, Rare, and Super as
 * printed. Ultra and Secret roll the chase table below.
 */
export const CARD_FOIL_IDS = [
  'common',
  'rare',
  'super',
  'ultra',
  'ghost',
  'helios',
  'nightglass',
  'stormwake',
] as const

export type CardFoilId = (typeof CARD_FOIL_IDS)[number]

export const DEFAULT_CARD_FOIL: CardFoilId = 'common'

/** Lower rank = higher rarity (Stormwake Rare first, Common last). */
export const CARD_FOIL_RANK: Record<CardFoilId, number> = {
  stormwake: 0,
  nightglass: 1,
  helios: 2,
  ghost: 3,
  ultra: 4,
  super: 5,
  rare: 6,
  common: 7,
}

export const CARD_FOIL_IDS_BEST_FIRST: CardFoilId[] = [...CARD_FOIL_IDS].sort(
  (a, b) => CARD_FOIL_RANK[a] - CARD_FOIL_RANK[b],
)

/**
 * Master Pack odds (weights / 10000). That pack still rolls this on each
 * of its 5 cards. LOB and Metal Raiders use the 2002 slot table instead.
 *
 * Common 78.06 / Rare 15.00 / Super 4.50 / Ultra 1.80 /
 * Ghost = Helios = Nightglass = Stormwake 0.16 each (~1/125 packs).
 *
 * In a 5-card shop pack that is about: 3.9 Commons, 0.75 Rare, a Super
 * every ~4–5 packs, Ultra ~1/11, and each house chase (Ghost included) ~1/125.
 * The 20-card starters use CARD_FOIL_STARTER_WEIGHTS. The 5-card Extra
 * Deck starter uses this shop table.
 */
export const CARD_FOIL_PACK_WEIGHTS: Record<CardFoilId, number> = {
  common: 7806,
  rare: 1500,
  super: 450,
  ultra: 180,
  ghost: 16,
  helios: 16,
  nightglass: 16,
  stormwake: 16,
}

/** Shop booster size the pack table is balanced around. */
export const SHOP_PACK_CARD_COUNT = 5

/** Main starter size (monsters / spells-traps). Extra Deck starter is 5. */
export const STARTER_MAIN_PACK_CARD_COUNT = 20

/**
 * Scale non-Common weights so a pack of `packSize` keeps the same expected
 * rarity counts as a shop pack. Leftover weight goes to Common.
 */
function scaleFoilWeights(
  base: Record<CardFoilId, number>,
  packSize: number,
): Record<CardFoilId, number> {
  const scale = SHOP_PACK_CARD_COUNT / packSize
  const next = { ...base }
  let spent = 0
  for (const id of CARD_FOIL_IDS) {
    if (id === 'common') continue
    const w = Math.max(0, Math.round(base[id] * scale))
    next[id] = w
    spent += w
  }
  const total = CARD_FOIL_IDS.reduce((sum, id) => sum + base[id], 0)
  next.common = Math.max(0, total - spent)
  return next
}

/**
 * Kept for the old starter table. Starters now use the card's highest
 * printed rarity, then the chase table when that rarity is Ultra or Secret.
 *
 * Common 94.51 / Rare 3.75 / Super 1.13 / Ultra 0.45 /
 * Ghost = Helios = Nightglass = Stormwake 0.04 each.
 */
export const CARD_FOIL_STARTER_WEIGHTS: Record<CardFoilId, number> =
  scaleFoilWeights(CARD_FOIL_PACK_WEIGHTS, STARTER_MAIN_PACK_CARD_COUNT)

/**
 * Rolls one house rarity from an explicit weight table.
 */
export function rollFoilFrom(
  weights: Record<CardFoilId, number>,
  random: () => number = Math.random,
): CardFoilId {
  const total = CARD_FOIL_IDS.reduce((sum, id) => sum + weights[id], 0)
  let n = random() * total
  for (const id of CARD_FOIL_IDS) {
    n -= weights[id]
    if (n <= 0) return id
  }
  return DEFAULT_CARD_FOIL
}

/**
 * Rolls one house rarity using the shop pack table.
 */
export function rollCardFoil(random: () => number = Math.random): CardFoilId {
  return rollFoilFrom(CARD_FOIL_PACK_WEIGHTS, random)
}

/**
 * Once a card's highest printing is Ultra or Secret, the foil is one of
 * these. Weights match the pack table among this slice: Ultra 180,
 * Ghost = Helios = Nightglass = Stormwake 16.
 */
export const CARD_FOIL_CHASE_WEIGHTS: Record<
  'ultra' | 'ghost' | 'helios' | 'nightglass' | 'stormwake',
  number
> = {
  ultra: 180,
  ghost: 16,
  helios: 16,
  nightglass: 16,
  stormwake: 16,
}

const CHASE_FOIL_IDS = ['ultra', 'ghost', 'helios', 'nightglass', 'stormwake'] as const

/** Rolls Ultra, Ghost, Helios, Nightglass, or Stormwake. */
export function rollChaseFoil(random: () => number = Math.random): CardFoilId {
  const total = CHASE_FOIL_IDS.reduce(
    (sum, id) => sum + CARD_FOIL_CHASE_WEIGHTS[id],
    0,
  )
  let n = random() * total
  for (const id of CHASE_FOIL_IDS) {
    n -= CARD_FOIL_CHASE_WEIGHTS[id]
    if (n <= 0) return id
  }
  return 'ultra'
}

export function isCardFoilId(value: unknown): value is CardFoilId {
  return (
    typeof value === 'string' &&
    (CARD_FOIL_IDS as readonly string[]).includes(value)
  )
}

/** Official / legacy labels that are not house ids. */
const FOIL_ALIASES: Record<string, CardFoilId> = {
  ultra_rare: 'ultra',
  'ultra rare': 'ultra',
  super_rare: 'super',
  'super rare': 'super',
  short_print: 'common',
  'short print': 'common',
}

export function asCardFoil(value: unknown): CardFoilId {
  if (isCardFoilId(value)) return value
  if (typeof value !== 'string') return DEFAULT_CARD_FOIL
  const key = value.trim().toLowerCase().replace(/-/g, '_')
  const spaced = key.replace(/_/g, ' ')
  return FOIL_ALIASES[key] ?? FOIL_ALIASES[spaced] ?? DEFAULT_CARD_FOIL
}

/**
 * True when this print has a visible foil overlay (everything except Common).
 */
export function foilHasOverlay(foil: CardFoilId): boolean {
  return foil !== 'common'
}
